/**
 * Store-free helpers for the scenario's coordinate system.
 *
 * The coordinate system describes the *data*, not the problem being solved,
 * so it lives in `generalSettings.mapping.coordinateSystem` and any model can
 * use either kind. These take the `inputData.present` slice directly and
 * import nothing, so request builders, cost helpers, and unit tests can use
 * them without pulling in the store. Components should use the equivalent
 * selectors (`selectCoordinateSystem` / `selectIsCartesian` in mapSelectors).
 */
import { isFiniteNumeric } from 'functions/numbers';

/** Latitude/longitude bounds — the limits that make a coordinate valid on a map. */
const MAX_LATITUDE = 90;
const MAX_LONGITUDE = 180;

/**
 * The scenario's coordinate system.
 *
 * @param {Object} state - The `inputData.present` slice.
 * @returns {'geographic'|'cartesian'} Defaults to 'geographic' when unset, so
 *   scenarios saved before the setting existed keep their map behavior.
 */
export function getCoordinateSystem(state) {
  return state?.generalSettings?.mapping?.coordinateSystem ?? 'geographic';
}

/** @returns {boolean} True when the scenario uses cartesian x-y coordinates. */
export function isCartesian(state) {
  return getCoordinateSystem(state) === 'cartesian';
}

// Shared guard: a cell of only spaces used to pass this and read as 0.
const isFiniteCoord = (value) => isFiniteNumeric(value);

/**
 * Locations whose coordinates can't be interpreted as latitude/longitude.
 *
 * Switching a cartesian scenario to geographic reinterprets the same numbers
 * as lat/lng, and plane coordinates are routinely outside those bounds — such
 * points would fail validation and be misplaced on the map. Blank or
 * non-numeric coordinates are ignored: they're incomplete rows, not wrong
 * ones, and already surface through normal row validation.
 *
 * @param {Array<Object>} locations - Location rows (`latitude`/`longitude`).
 * @returns {Array<Object>} The offending rows (empty when all are usable).
 */
export function findOutOfRangeLocations(locations) {
  const offenders = [];
  for (const row of locations || []) {
    const lat = row?.latitude;
    const lng = row?.longitude;
    if (!isFiniteCoord(lat) || !isFiniteCoord(lng)) continue;
    if (Math.abs(Number(lat)) > MAX_LATITUDE || Math.abs(Number(lng)) > MAX_LONGITUDE) {
      offenders.push(row);
    }
  }
  return offenders;
}
